import { isValidElement, ReactNode } from "react";
import { flattenChildren } from "../flattenChildren";
import styles from "./TruncatedRow.module.css";

type TruncatedText = React.ReactElement & {
  type: { __truncatedMarker: symbol };
};

export interface TruncatedRowProps {
  children: React.ReactNode;
}

const truncatedMarker = Symbol("truncatedMarker");

/** Type guard to identify `TruncatedRow.Truncated` elements. */
function isTruncatedElement(node: ReactNode): node is TruncatedText {
  return (
    isValidElement(node) &&
    (typeof node.type === "object" || typeof node.type === "function") &&
    "__truncatedMarker" in node.type &&
    node.type.__truncatedMarker === truncatedMarker
  );
}

function Root({ children }: TruncatedRowProps) {
  const flattenedChildren = flattenChildren(children);

  const normalized = flattenedChildren.flatMap((child, index) => {
    // Pass through our Truncated child unchanged
    if (isTruncatedElement(child)) return child;

    // Wrap everything else (strings, numbers, other elements) as fixed
    if (typeof child === "string" || typeof child === "number") {
      return (
        <span key={index} className={styles.fixed}>
          {child}
        </span>
      );
    }

    const key = isValidElement(child) && child.key ? child.key : index;

    return (
      <span key={key} className={styles.fixed}>
        {child}
      </span>
    );
  });

  return <div className={styles.container}>{normalized}</div>;
}

function Truncated({ children }: TruncatedRowProps): TruncatedText {
  return <span className={styles.truncated}>{children}</span>;
}

Truncated.__truncatedMarker = truncatedMarker;

export const TruncatedRow = {
  /** A component that provides mid-line truncation for long text. */
  Root,
  /** A marker component that indicates which text should be truncated. */
  Truncated,
};
